import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { classNames } from "primereact/utils";
import { useRef, useEffect, useState } from "react";
import { sendChatMessage, sendMessageCreatingChat } from "../services/chat";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Toast } from "primereact/toast";
import { useChatMessages } from "../hooks/queries";
import { Divider } from "primereact/divider";

/**
 * Vista de mensajes de chat
 * @param {object} props
 * @param {number?} props.chatId
 * @param {number?} props.userForNewChat
 * @param {(chat: any) => void} props.onChatCreated
 * @param {React.RefObject<HTMLDivElement>} props.contentRef
 */
export default function ChatMessages({
  chatId,
  userForNewChat,
  onChatCreated,
  contentRef,
}) {
  const toastRef = useRef(null);

  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState("");

  const messagesQuery = useChatMessages({
    chatId,
    options: {
      enabled: !!chatId,
      initialPageParam: 1,
      select: (data) => ({
        pages: data.pages.map((page) => ({
          ...page,
          results: [...page.results].reverse(),
        })),
        pageParams: data.pageParams,
      }),
    },
  });

  const queryClient = useQueryClient();
  const prevMessagesRef = useRef([]);
  const prevScrollHeightRef = useRef(0);
  const loadingMoreRef = useRef(false);

  useEffect(() => {
    // Guardar mensajes en el estado local al recibirlos (prepend)
    if (messagesQuery.data) {
      const results = messagesQuery.data.pages
        .map((page) => page.results)
        .reduce((acc, curr) => curr.concat(acc), []);
      setMessages(results);
    }
  }, [messagesQuery.data]);

  useEffect(() => {
    // Ajustar scroll si se cargaron mensajes antiguos
    if (loadingMoreRef.current && contentRef.current) {
      const newScrollHeight = contentRef.current.scrollHeight;
      contentRef.current.scrollTop =
        newScrollHeight - prevScrollHeightRef.current;
      loadingMoreRef.current = false;
    }
  }, [messages, contentRef]);

  useEffect(() => {
    // Desplazar hacia abajo solo si se agregó un mensaje nuevo al final
    const prevMessages = prevMessagesRef.current;
    const prevLastId =
      prevMessages.length > 0
        ? prevMessages[prevMessages.length - 1]?.id
        : null;
    const currLastId =
      messages.length > 0 ? messages[messages.length - 1]?.id : null;
    if (
      messages.length > prevMessages.length &&
      prevLastId !== currLastId &&
      !loadingMoreRef.current
    ) {
      if (contentRef.current) {
        contentRef.current.scrollTop = contentRef.current.scrollHeight;
      }
    }
    prevMessagesRef.current = messages;
  }, [messages, contentRef]);

  useEffect(() => {
    const handleScroll = () => {
      if (!contentRef.current) return;
      if (contentRef.current.scrollTop === 0) {
        if (messagesQuery.hasNextPage && !messagesQuery.isFetching) {
          // Save scrollHeight before loading more
          prevScrollHeightRef.current = contentRef.current.scrollHeight;
          loadingMoreRef.current = true;
          messagesQuery.fetchNextPage();
        }
      }
    };
    const ref = contentRef.current;
    if (ref) {
      ref.addEventListener("scroll", handleScroll);
    }
    return () => {
      if (ref) {
        ref.removeEventListener("scroll", handleScroll);
      }
    };
  }, [contentRef, messagesQuery]);

  const sendMessageMutation = useMutation({
    mutationFn: sendChatMessage,
    onSuccess: ({ type, message, data }) => {
      if (type === "error") {
        toastRef.current.show({
          severity: "error",
          summary: "Error",
          detail: message || "No se pudo enviar el mensaje.",
        });
      } else {
        setMessageInput("");
        setMessages((prev) => [...prev, data]);
        queryClient.invalidateQueries({ queryKey: ["chats"] });
      }
    },
    onError: (error) => {
      console.error("Error al enviar el mensaje:", error);
      toastRef.current.show({
        severity: "error",
        summary: "Error",
        detail: "No se pudo enviar el mensaje, intente de nuevo.",
      });
    },
  });

  const createChatAndSendMessageMutation = useMutation({
    mutationFn: sendMessageCreatingChat,
    onSuccess: ({ type, message, chat, chatMessage }) => {
      if (type === "error") {
        toastRef.current.show({
          severity: "error",
          summary: "Error",
          detail: message || "No se pudo crear el chat y enviar el mensaje.",
        });
      } else {
        onChatCreated(chat);
        setMessageInput("");
        setMessages((prev) => [...prev, chatMessage]);
        queryClient.invalidateQueries({ queryKey: ["chats"] });
      }
    },
  });

  const onSendMessage = () => {
    if (messageInput.trim() === "") return;
    if (chatId) {
      sendMessageMutation.mutate({
        chatId,
        content: messageInput.trim(),
      });
    } else if (userForNewChat) {
      createChatAndSendMessageMutation.mutate({
        content: messageInput.trim(),
        userId: userForNewChat,
      });
    }
    return;
  };

  return (
    <div className="flex flex-column">
      <Toast ref={toastRef} position="top-center" />
      <div
        className="flex flex-column flex-1 overflow-y-auto py-2 px-3"
        style={{
          maxHeight: "calc(100vh - 220px)",
          minHeight: messages.length > 0 ? "calc(100vh - 220px)" : undefined,
        }}
        ref={contentRef}
      >
        {messages?.map((msg, idx) => {
          const prevMsg = messages[idx - 1];
          // Helper to get date string (YYYY-MM-DD)
          const getDateStr = (ts) =>
            ts ? new Date(ts).toISOString().slice(0, 10) : "";
          const showHr =
            idx === 0 ||
            getDateStr(msg.timestamp) !== getDateStr(prevMsg?.timestamp);
          return (
            <div key={idx}>
              {showHr && (
                <div className="flex align-items-center mb-3 mt-2">
                  <Divider className="flex-1" />
                  <span className="mx-2 text-xs text-gray-500 white-space-nowrap">
                    {new Date(msg.timestamp).toLocaleDateString()}
                  </span>
                  <Divider className="flex-1" />
                </div>
              )}
              <Message message={msg} />
            </div>
          );
        })}
        {(chatId || userForNewChat) && messages.length === 0 && (
          <div
            className="text-center text-gray-500 flex align-items-center justify-content-center"
            style={{ height: "90vh" }}
          >
            No hay mensajes en este chat.
          </div>
        )}
        {!chatId && !userForNewChat && (
          <div
            className="text-center text-gray-500 flex align-items-center justify-content-center"
            style={{ height: "90vh" }}
          >
            Selecciona un chat para ver los mensajes.
          </div>
        )}
      </div>
      {(chatId || userForNewChat) && (
        <div className="border-top-1 border-200 p-2 pb-3 surface-50">
          <div className="p-inputgroup flex-1">
            <InputText
              placeholder="Ingresa un mensaje"
              className="w-full"
              value={messageInput}
              onChange={(e) => setMessageInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  onSendMessage();
                }
              }}
              disabled={
                sendMessageMutation.isPending ||
                createChatAndSendMessageMutation.isPending ||
                (!chatId && !userForNewChat)
              }
            />
            {/* <Button
            icon="pi pi-paperclip"
            severity="secondary"
            tooltip="Adjuntar archivo"
            tooltipOptions={{ position: "top" }}
            aria-label="Adjuntar archivo"
            disabled={readyState !== ReadyState.OPEN}
          /> */}
            <Button
              icon="pi pi-send"
              aria-label="Enviar mensaje"
              disabled={
                messageInput === "" ||
                sendMessageMutation.isPending ||
                createChatAndSendMessageMutation.isPending ||
                (!chatId && !userForNewChat)
              }
              onClick={onSendMessage}
            />
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Message bubble
 * @param {object} props
 * @param {import("../services/chat").ChatMessage} props.message
 */
function Message({ message }) {
  // Helper to format timestamp as HH:mm
  function formatTime(ts) {
    if (!ts) return "";
    const date = new Date(ts);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }

  return (
    <div
      className={classNames("flex mb-2", {
        "justify-content-end": message.is_by_user,
        "justify-content-start": !message.is_by_user,
      })}
    >
      <div
        className={classNames("border-round-top shadow-1 text-left py-2 px-3", {
          "border-round-bottom-right": !message.is_by_user,
          "border-round-bottom-left": message.is_by_user,
        })}
        style={{
          maxWidth: "70%",
          background: message.is_by_user ? "#c3edf5" : "#f1f0f0",
          wordBreak: "break-word",
        }}
      >
        <div>{message.content}</div>
        <div
          style={{
            fontSize: "0.75rem",
            color: "#888",
            marginTop: 4,
            textAlign: "right",
          }}
        >
          {formatTime(message.timestamp)}
        </div>
      </div>
    </div>
  );
}
